import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useProducts } from "../../../../hooks/useProducts/useProducts";
import { useUserContext } from "../../../../providers/UserProvider";
import { ProductDropdown } from "../../ProductDropdown/ProductDropdown";
import { IOrganizationProductInstance, OrganizationProduct } from "../../../../domain/OrganizationProduct";
import styles from "./AddProductAccessForm.module.scss";
import { InstanceDropdown } from "../../InstanceDropdown/InstanceDropdown";
import classnames from "classnames";
import { AccessManagementUserFormIconButton } from "../AccessManagementUserForm/AccessManagementUserFormIconButton";
import { RoleSelector } from "../../RoleSelector/RoleSelector";
import { Role } from "../../../../domain/Role";
import {
    ADMINCENTER_PRODUCT_ID,
    ALL_PAGES_SIZE,
    ALL_PROJECTS_ID,
    ALL_PROJECTS_NAME,
    AMBIGUOUS_PRODUCT_GROUP_ERROR_MESSAGE,
    ATTRIBUTE_ROLES,
    CONTEXT_SCOPES,
    GROUP_TYPES
} from "../../../../constants/constants";
import { ProductAttribute } from "../../../../domain/ProductAttribute";
import { useAttributes } from "../../../../hooks/useAttributes/useAttributes";
import { ProjectsDropdown } from "../../ProjectsDropdown/ProjectsDropdown";
import { User } from "../../../../domain/User";
import { useAccessList } from "../../../../hooks/useAccessList/useAccessList";
import {
    findByField,
    getProductGroupResolutionErrorKey,
    getSystemRoles,
    resolveProductGroupForRole
} from "../../../../lib/access-helpers";
import { UserGroup } from "../../../../domain/UserGroup";
import { useRoles } from "../../../../hooks/useRoles/useRoles";
import { Attention } from "@optimizely/axiom";
import { useAttribute } from "../../../../hooks/useAttribute/useAttribute";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../../feature-flags/flags";
import { OpalRoleSuggestionSelection } from "../../OpalRoleSuggestionSelection/OpalRoleSuggestionSelection";
import { useOrgUserGroups } from "../../../../hooks/useOrgUserGroups/useOrgUserGroups";
import { useOrganizationSettings } from "../../../../hooks/useOrganizationSettings/useOrganizationSettings";
import { useAccessFlowUserFormContext } from "../AccessManagementUserForm/AccessManagementUserFormProvider";
import { AccessDetailsByProductId } from "../../../../domain/AccessDetails";
import { datadogRum } from "@datadog/browser-rum";

export type AddProductAccessPermission = {
    product?: OrganizationProduct;
    instance?: IOrganizationProductInstance;
    group: UserGroup;
    role: Role;
    project?: ProductAttribute;
};

type AddProductAccessFormProps = {
    className?: string;
    existingAccess?: AccessDetailsByProductId;
    instance?: IOrganizationProductInstance | undefined;
    onSubmit: ({ permissions }: { permissions: AddProductAccessPermission[] }) => void;
    onClose: ({ refetchAccess }: { refetchAccess?: boolean }) => void;
    product?: OrganizationProduct | undefined;
    project?: ProductAttribute | undefined;
    role?: Role | undefined;
    user?: User;
    isSaving?: boolean;
};

type AccessFieldsState = {
    product: OrganizationProduct | undefined;
    instance: IOrganizationProductInstance | undefined;
    project?: ProductAttribute;
    role: Role | undefined;
};

type AccessFieldsAction = {
    field: "product" | "instance" | "reset" | "role" | "project" | "multi";
    value?:
        | OrganizationProduct
        | IOrganizationProductInstance
        | Role
        | ProductAttribute
        | {
              product?: OrganizationProduct;
              instance?: IOrganizationProductInstance;
              role?: Role;
              project?: ProductAttribute;
          };
};

const accessFieldsReducer = (state: AccessFieldsState, action: AccessFieldsAction): AccessFieldsState => {
    switch (action.field) {
        case "instance":
            return {
                ...state,
                instance: action.value as IOrganizationProductInstance,
                project: undefined,
                role: undefined
            };
        case "product":
            return { ...state, product: action.value as OrganizationProduct };
        case "project":
            return { ...state, project: action.value as ProductAttribute };

        case "reset":
            return {
                product: action.value ? (action.value as OrganizationProduct) : undefined,
                instance: undefined,
                role: undefined
            };
        case "role":
            return { ...state, role: action.value as Role };
        case "multi":
            return { ...state, ...action.value };
        default:
            return state;
    }
};

export const AddProductAccessForm = ({
    className,
    existingAccess,
    instance,
    onSubmit,
    onClose,
    product,
    role,
    user,
    isSaving
}: AddProductAccessFormProps) => {
    const { userGroupIds } = useAccessFlowUserFormContext();
    const { accessContext, organizationId, canUserDoAction } = useUserContext();
    const { products = [], isLoading: isLoadingInstances } = useProducts({ organizationId });
    const { settings } = useOrganizationSettings({ organizationId });
    const { genAI } = settings || {};
    const { enabled: genAIOrgEnabled } = genAI || {};
    const [dismissedOpalSuggestions, setDismissedOpalSuggestions] = useState(false);
    const [additionalOpalGroup, setAdditionalOpalGroup] = useState<{
        existingGroup: UserGroup | null;
        selectedGroup: UserGroup | null;
        selectedInstanceId?: string | null;
        selectedRoleId?: string | null;
    }>({ existingGroup: null, selectedGroup: null, selectedInstanceId: null, selectedRoleId: null });

    // Feature flag for Opal auto-provisioning
    const { enabled: opalAutoProvisioningEnabled } = useFeatureFlag(Flags.ENABLE_OPAL_AUTO_PROVISIONING);
    const initialState: AccessFieldsState = {
        product: product,
        instance: instance,
        role: role
    };
    const [excludeOpalRole, setExcludeOpalRole] = useState<boolean>(false);
    const [unresolvableGroup, setUnresolvableGroup] = useState<boolean>(false);
    const [accessFields, updateFieldValue] = useReducer(accessFieldsReducer, initialState);
    const { product: productField, instance: instanceField, role: roleField, project: projectField } = accessFields;
    const isExp = productField?.id === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID;
    const skipAutoSelectRef = useRef(false);
    const fetchedInstancesRef = useRef<Record<string, boolean>>({});

    const loadedContextAwareState = useRef(false);
    const sortedProducts = [...products].sort((a, b) => a.name.localeCompare(b.name));
    const opalProduct = sortedProducts.find((p) => p.id === process.env.REACT_APP_OPAL_PRODUCT_ID);

    // Create a memoized map for O(1) instance-to-product lookup
    const instanceToProductMap = useMemo(() => {
        const map = new Map<string, OrganizationProduct>();
        products.forEach((product) => {
            product.instances?.forEach((instance) => {
                map.set(instance.id, product);
            });
        });
        return map;
    }, [products]);

    const { accessList: instanceGroupPermissions, isLoading: isLoadingAvailableRoles } = useAccessList({
        organizationId,
        instanceIds: instanceField?.id ? [instanceField?.id] : []
    });
    const { roles: opalRoles } = useRoles({
        organizationId,
        productId: process.env.REACT_APP_OPAL_PRODUCT_ID,
        skip: !genAIOrgEnabled || !opalAutoProvisioningEnabled // skip if org has gen ai disabled
    });

    const { userGroups: orgOpalProductGroups } = useOrgUserGroups({
        organizationId,
        roleIds: opalRoles?.map((r) => r.id),
        groupTypes: [GROUP_TYPES.PRODUCT],
        skip: !(opalRoles?.length && opalAutoProvisioningEnabled) // don't make the extra call if we don't need it.
    });

    const {
        getExperimentationProjects,
        attributes: projects,
        isLoadingInitialData: isLoadingProjects
    } = useAttributes({
        productId: productField?.id,
        instanceId: instanceField?.id,
        size: ALL_PAGES_SIZE
    });

    const [contextProjectId, setContextProjectId] = useState<string | undefined>(undefined);
    const { attribute: project } = useAttribute({ attributeId: contextProjectId });
    const [userAccessibleInstances, setUserAccessibleInstances] = useState<IOrganizationProductInstance[]>([]);
    const [isLoadingUserAccessibleInstances, setIsLoadingUserAccessibleInstances] = useState<boolean>(false);
    const opalAutoProvisioningApplicable =
        !!instanceField?.genAIEnabled &&
        opalAutoProvisioningEnabled &&
        productField?.id !== process.env.REACT_APP_OPAL_PRODUCT_ID &&
        (orgOpalProductGroups?.length || 0) > 0 &&
        !dismissedOpalSuggestions;
    const currentAccessForInstance =
        instanceField?.id && existingAccess ? existingAccess[productField!.id]?.instanceAccess[instanceField.id] : null;

    // Get existing OPAL access for the OpalRoleSuggestionSelection component
    const existingOpalAccess = existingAccess?.[process.env.REACT_APP_OPAL_PRODUCT_ID!]?.instanceAccess || null;

    useEffect(() => {
        if (!orgOpalProductGroups?.length) return;

        // Auto select only if there is exactly one Opal instance AND no pre-existing group
        const singleOpalInstance = (opalProduct?.instances?.length || 0) === 1;
        const fallbackUserGroup = orgOpalProductGroups.find((g) => /user/i.test(g.name)) || null;

        if (!user) {
            setAdditionalOpalGroup({
                existingGroup: null,
                selectedGroup: singleOpalInstance ? fallbackUserGroup : null,
                selectedInstanceId: null,
                selectedRoleId: null
            });
            return;
        }

        const preExistingOpalRoleGroup = orgOpalProductGroups.find((group) => userGroupIds?.includes(group.id));

        setAdditionalOpalGroup({
            existingGroup: preExistingOpalRoleGroup || null,
            selectedGroup: preExistingOpalRoleGroup
                ? preExistingOpalRoleGroup
                : singleOpalInstance
                ? fallbackUserGroup
                : null,
            selectedInstanceId: null,
            selectedRoleId: null
        });
    }, [orgOpalProductGroups, user, opalProduct, userGroupIds]);

    useEffect(() => {
        skipAutoSelectRef.current = false; // reset auto-select flag when product changes
        // Clear the cache for all products when product changes to ensure fresh data
        clearInstancesCache();
    }, [productField?.id]);

    const clearInstancesCache = () => {
        fetchedInstancesRef.current = {};
    };

    // Any change to the selection invalidates a previous resolution failure. Resolution depends on
    // (instance, role) only, so the project is not a dependency — and changing it clears the role
    // anyway, which fires this effect.
    useEffect(() => {
        setUnresolvableGroup(false);
    }, [productField?.id, instanceField?.id, roleField?.id]);

    useEffect(() => {
        if (!productField?.id) return;

        if (fetchedInstancesRef.current[productField.id]) {
            return; // already fetched → skip
        }

        const selectedProductInstances = products.filter((p) => p.id === productField.id).flatMap((p) => p.instances);

        const canAccessProduct = canUserDoAction({
            action: [ATTRIBUTE_ROLES.GROUPS.MANAGE, ATTRIBUTE_ROLES.GROUPS.UPDATE],
            context: { ProductId: productField.id }
        });
        const canAccessInstance = ({ instance }: { instance: IOrganizationProductInstance }) => {
            return canUserDoAction({
                action: [ATTRIBUTE_ROLES.GROUPS.MANAGE, ATTRIBUTE_ROLES.GROUPS.UPDATE],
                context: { InstanceId: instance.id }
            });
        };
        const canAccessProject = ({ project }: { project: ProductAttribute }) => {
            return canUserDoAction({
                action: [ATTRIBUTE_ROLES.GROUPS.MANAGE, ATTRIBUTE_ROLES.GROUPS.UPDATE],
                context: { AttributeId: project.id }
            });
        };

        setIsLoadingUserAccessibleInstances(true);

        if (isExp) {
            Promise.all(
                selectedProductInstances.map(async (instance) => {
                    const projects = await getExperimentationProjects({ instanceId: instance.id });
                    if (
                        !!instance.optiIdEnabled &&
                        (canAccessInstance({ instance }) ||
                            canAccessProduct ||
                            projects?.some((p) => canAccessProject({ project: p })))
                    ) {
                        return instance;
                    }
                    return null;
                })
            )
                .then((results) => {
                    const allowedInstances = results.filter(Boolean) as IOrganizationProductInstance[];
                    setUserAccessibleInstances(allowedInstances);
                    fetchedInstancesRef.current[productField.id!] = true;
                })
                .finally(() => {
                    setIsLoadingUserAccessibleInstances(false);
                });
        } else {
            const allowedInstances = selectedProductInstances.filter((instance) => {
                return !!instance.optiIdEnabled && (canAccessInstance({ instance }) || canAccessProduct);
            });
            setUserAccessibleInstances(allowedInstances);
            fetchedInstancesRef.current[productField.id!] = true;
            setIsLoadingUserAccessibleInstances(false);
        }
    }, [products, productField?.id, canUserDoAction, getExperimentationProjects, isExp]);

    // Groups fetched for the selected instance also carry their permissions on other instances
    // (Admin Center, linked Experimentation instances, sibling instances). Role ids are
    // product-global, so everything below has to be scoped to the selected instance or we end up
    // offering — and granting — roles that belong to a different instance entirely.
    const instanceAccessForSelectedInstance =
        instanceGroupPermissions
            ?.flatMap((pp) => pp.instanceAccess)
            .filter((ia) => ia.instance?.id === instanceField?.id) || [];

    const uniqueRoleIds = new Set(); // Create a Set to track unique role ids
    const roles =
        instanceAccessForSelectedInstance
            ?.flatMap((ia) => ia.availableRoles)
            ?.filter((ar) => ar.group.groupType === GROUP_TYPES.PRODUCT)
            ?.map((ar) => ar.role)
            ?.filter((role) => {
                if (uniqueRoleIds.has(role.id)) {
                    return false;
                } else {
                    uniqueRoleIds.add(role.id);
                }
                if (projectField?.id === ALL_PROJECTS_ID) {
                    return role.isInstanceRole;
                }
                // if a project is selected, filter out any role that is not tied to the project
                const roleForProject =
                    !projectField?.id ||
                    (!role.isInstanceRole && role.attributes?.some((attr) => attr.id === projectField.id));
                return roleForProject;
            }) || [];
    const roleIdsInGroups = roles.map((role) => role.id);

    // Get all product roles so we can later filter
    const { roles: productRoles, isLoading: isLoadingProductRoles } = useRoles({
        organizationId,
        productId: productField?.id,
        instanceId: instanceField?.id,
        projectId: projectField?.id,
        size: ALL_PAGES_SIZE,
        skip: !(productField?.id && instanceField?.id) || (isExp && !projectField?.id)
    });

    let currentProductRoles = useMemo(() => {
        return productRoles ?? [];
    }, [productRoles]);

    // Admin Center should not show any Project Owner roles or User Manager roles
    // Or any roles that are not system generated
    if (productField?.id === ADMINCENTER_PRODUCT_ID) {
        currentProductRoles = getSystemRoles({ roles: currentProductRoles, productId: productField?.id });
    }
    if (isExp) {
        currentProductRoles = getSystemRoles({
            roles: currentProductRoles,
            projectId: projectField?.id,
            productId: productField?.id
        });
    }

    let adjustedRoles = currentProductRoles.filter((r) => roleIdsInGroups.includes(r.id));
    adjustedRoles = adjustedRoles.sort((a, b) => a.displayName.localeCompare(b.displayName));

    useEffect(() => {
        if (!accessContext?.scope) {
            updateFieldValue({ field: "reset" });
        }
    }, [user, accessContext?.scope]);

    useEffect(() => {
        if (productField?.id) {
            updateFieldValue({
                field: "reset",
                value: productField
            });
        }
    }, [productField]);

    // auto select the instance if there is only one
    useEffect(() => {
        if (userAccessibleInstances.length === 1 && !skipAutoSelectRef.current) {
            updateFieldValue({
                field: "instance",
                value: userAccessibleInstances[0] as IOrganizationProductInstance
            });
        }
    }, [userAccessibleInstances]);

    // auto select the role if there is only one
    useEffect(() => {
        if (instanceField?.id && adjustedRoles?.length === 1 && roleField?.id !== adjustedRoles[0].id) {
            updateFieldValue({
                field: "role",
                value: adjustedRoles[0] as Role
            });
        }
    }, [roleField?.id, adjustedRoles, instanceField?.id]);

    useEffect(() => {
        const { scope, items = [] } = accessContext || {};

        const productFromScope = products?.find((p) => p.id === items[0]?.id);
        if (
            scope === CONTEXT_SCOPES.PRODUCT &&
            items?.length &&
            productField?.id !== items[0]?.id &&
            productFromScope &&
            !loadedContextAwareState.current
        ) {
            // handle product context
            updateFieldValue({
                field: "reset",
                value: productFromScope
            });
            loadedContextAwareState.current = true;
        } else if (scope === CONTEXT_SCOPES.INSTANCE && items?.length && !loadedContextAwareState.current) {
            // look up the product from the instance id using O(1) map lookup
            const instanceId = items[0].id;
            const productFromInstance = instanceToProductMap.get(instanceId);
            const instanceDetails = findByField({ value: instanceId, items: userAccessibleInstances, field: "id" });
            if (productFromInstance?.id !== productField?.id) {
                updateFieldValue({
                    field: "reset",
                    value: productFromInstance
                });
            }

            // if the user does not have access to the instance they are viewing for some reason,
            // we shouldn't allow them to do the preselection with the instance. This may result in a
            // "No instances" state but should be addressed (ie why is the user allowed to view this instance?)
            if (!instanceDetails) return;
            updateFieldValue({
                field: "instance",
                value: instanceDetails
            });
            loadedContextAwareState.current = true;
        } else if (scope === CONTEXT_SCOPES.PROJECT && items?.length && !loadedContextAwareState.current) {
            const projectId = items[0].id;
            const experimentationProduct = products?.find(
                (p) => p.id === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID
            );
            // call this if the exp product hasn't been selected
            if (experimentationProduct && productField?.id !== experimentationProduct?.id) {
                updateFieldValue({ field: "reset", value: experimentationProduct });
            }

            if (projectId) {
                setContextProjectId(projectId);
            }
        }
    }, [accessContext, productField?.id, products, user, userAccessibleInstances, project, instanceToProductMap]);

    useEffect(() => {
        const projectInstance = userAccessibleInstances?.find((i) => i.id === project?.instanceId);
        if (projectInstance) {
            updateFieldValue({
                field: "multi",
                value: {
                    instance: projectInstance,
                    project: project as ProductAttribute
                }
            });
            loadedContextAwareState.current = true;
        }
    }, [project, userAccessibleInstances]);

    const handleProductChange = ({ product: selectedProduct }: { product: OrganizationProduct }) => {
        updateFieldValue({ field: "reset", value: selectedProduct });
    };

    const handleInstanceChange = ({
        value: instance
    }: {
        value: IOrganizationProductInstance | IOrganizationProductInstance[] | null;
    }) => {
        skipAutoSelectRef.current = true;

        updateFieldValue({
            field: "instance",
            value: instance as IOrganizationProductInstance
        });
    };

    const handleRoleChange = (value: Role) => {
        updateFieldValue({
            field: "role",
            value: value as Role
        });
    };

    const handleProjectChange = ({ value }: { value: ProductAttribute | ProductAttribute[] | null }) => {
        updateFieldValue({
            field: "multi",
            value: {
                project: value as ProductAttribute,
                role: undefined
            }
        });
    };

    const handleOnSubmit = () => {
        const selectedInstance = products.flatMap((p) => p.instances).find((i) => i.id === instanceField?.id);

        if (!selectedInstance || !roleField) {
            setUnresolvableGroup(true);
            return;
        }

        // Role linking can leave many product groups offering the same role on the same instance.
        // Resolve to the native group rather than guessing, and refuse to write anything when the
        // target group is not unambiguous (EPTS-6364).
        const resolution = resolveProductGroupForRole({
            instance: selectedInstance,
            instanceAccess: instanceAccessForSelectedInstance,
            projectId: projectField?.id,
            role: roleField
        });

        if (resolution.status !== "resolved") {
            setUnresolvableGroup(true);
            datadogRum.addError(new Error(getProductGroupResolutionErrorKey(resolution.status)), {
                organizationId,
                instanceId: selectedInstance.id,
                roleId: roleField.id,
                resolution: resolution.status,
                candidateGroupIds: resolution.candidateGroupIds,
                candidateCount: resolution.candidateGroupIds.length,
                source: "AddProductAccessForm.handleOnSubmit"
            });
            return;
        }

        const selectedRole = resolution.match;
        const permissions: AddProductAccessPermission[] = [];

        // Add the primary permission that was selected
        const primaryPermission: AddProductAccessPermission = {
            product: productField!,
            instance: selectedInstance,
            group: selectedRole.group,
            role: selectedRole.role,
            project: projectField
        };
        permissions.push(primaryPermission);

        // If Opal auto-provisioning is enabled and should be triggered, add the Opal permission
        if (
            additionalOpalGroup.selectedGroup &&
            additionalOpalGroup.selectedGroup.id !== additionalOpalGroup.existingGroup?.id &&
            opalRoles?.length &&
            additionalOpalGroup.selectedInstanceId &&
            additionalOpalGroup.selectedRoleId
        ) {
            const opalProduct = sortedProducts.find((p) => p.id === process.env.REACT_APP_OPAL_PRODUCT_ID);
            const opalInstance = opalProduct?.instances?.find((i) => i.id === additionalOpalGroup.selectedInstanceId);
            const opalRole = opalRoles.find((r) => r.id === additionalOpalGroup.selectedRoleId);

            if (opalProduct && opalInstance && opalRole) {
                const opalPermission: AddProductAccessPermission = {
                    product: opalProduct,
                    instance: opalInstance,
                    group: additionalOpalGroup.selectedGroup,
                    role: opalRole
                };
                permissions.push(opalPermission);
            }
        }
        // Submit all permissions at once
        onSubmit({ permissions });
    };

    const handleOpalSuggestionChange = ({
        dismissed,
        value,
        instance,
        roleId
    }: {
        dismissed?: boolean;
        value: UserGroup | null;
        instance?: IOrganizationProductInstance | null;
        roleId?: string | null;
    }) => {
        if (dismissed) {
            setDismissedOpalSuggestions(true);
        }
        if (!value) {
            setExcludeOpalRole((prevState) => !prevState);
        }
        setAdditionalOpalGroup({
            ...additionalOpalGroup,
            selectedGroup: value,
            selectedInstanceId: instance?.id || null,
            selectedRoleId: roleId || null
        });
    };

    const allProjectsOption: ProductAttribute = {
        id: ALL_PROJECTS_ID,
        name: ALL_PROJECTS_NAME,
        description: "",
        instanceId: "",
        isActive: true,
        key: "",
        type: "",
        values: []
    };

    const hasAboveProjectLevelAccess =
        canUserDoAction({
            action: [ATTRIBUTE_ROLES.GROUPS.MANAGE, ATTRIBUTE_ROLES.GROUPS.UPDATE],
            context: { InstanceId: instanceField?.id }
        }) ||
        canUserDoAction({
            action: [ATTRIBUTE_ROLES.GROUPS.MANAGE, ATTRIBUTE_ROLES.GROUPS.UPDATE],
            context: { ProductId: productField?.id }
        });

    const allowedProjects =
        projects?.filter(
            (p) =>
                canUserDoAction({
                    action: [ATTRIBUTE_ROLES.GROUPS.MANAGE, ATTRIBUTE_ROLES.GROUPS.UPDATE],
                    context: { AttributeId: p.id }
                }) || hasAboveProjectLevelAccess
        ) || [];

    const projectsDropDownItems = hasAboveProjectLevelAccess
        ? [allProjectsOption, ...allowedProjects]
        : allowedProjects;

    const dropdownClasses = classnames(
        "dropdown-inherit-width",
        styles["add-user-access-form__selection-row-dropdown"]
    );

    const missingRole = !(productField && instanceField && roleField);
    const userHasOpalAssigned = !!existingOpalAccess && Object.keys(existingOpalAccess).length > 0;
    const needsOpalDecision =
        opalAutoProvisioningApplicable &&
        !excludeOpalRole &&
        !additionalOpalGroup.selectedGroup &&
        !dismissedOpalSuggestions &&
        !userHasOpalAssigned;

    const shouldDisableSave = missingRole || needsOpalDecision;
    const instancesLoading = !!productField?.id && (isLoadingInstances || isLoadingUserAccessibleInstances);
    const projectsLoading = Boolean(!!productField?.id && !!instanceField?.id && isLoadingProjects);
    const gettingRoles = isLoadingProductRoles || isLoadingAvailableRoles;
    const expRolesLoading = isExp && !!projectField?.id && gettingRoles;
    const nonExpRolesLoading = !isExp && !!productField?.id && !!instanceField?.id && gettingRoles;
    const rolesLoading = Boolean(nonExpRolesLoading || expRolesLoading);
    const rolesInitialized = !!productField?.id && !!instanceField?.id && !rolesLoading;

    return (
        <div className={classnames(className, styles["add-user-access-form"])}>
            <div className="flex push--bottom">
                <ProductDropdown
                    disabled={isSaving}
                    className={dropdownClasses}
                    onChange={handleProductChange}
                    products={sortedProducts}
                    white
                    value={productField?.id}
                />
                <InstanceDropdown
                    className={dropdownClasses}
                    disabled={!productField || isSaving || instancesLoading}
                    loading={instancesLoading}
                    instances={userAccessibleInstances}
                    onChange={handleInstanceChange}
                    value={instanceField?.id}
                    white
                />
                <div className={`${styles["add-user-access-form__actions"]}`} />
            </div>
            {isExp && (
                <div className="flex push--bottom">
                    <ProjectsDropdown
                        className={dropdownClasses}
                        disabled={!instanceField?.id || isSaving}
                        loading={projectsLoading}
                        onChange={handleProjectChange}
                        projects={projectsDropDownItems}
                        value={projectField?.id}
                        white
                    />
                    <div className={`${styles["add-user-access-form__actions"]}`} />
                </div>
            )}
            <div className={styles["add-user-access-form__selection-row"]}>
                <RoleSelector
                    isDisabled={!instanceField?.id || (isExp && !projectField?.id) || isSaving || rolesLoading}
                    loading={rolesLoading}
                    className={dropdownClasses}
                    selectedRole={roleField}
                    fullWidth={false}
                    onRoleChanged={handleRoleChange}
                    preExistingRoleIds={currentAccessForInstance?.flatMap((ia) => ia.roles?.map((r) => r.id)) || []}
                    roles={adjustedRoles}
                    isInitialized={rolesInitialized}
                />
                <div data-cy="access-form-actions" className={`${styles["add-user-access-form__actions"]}`}>
                    <AccessManagementUserFormIconButton
                        disabled={shouldDisableSave}
                        white={!isSaving}
                        icon="check"
                        onClick={handleOnSubmit}
                        loading={isSaving}
                    />
                    <AccessManagementUserFormIconButton
                        className="push--left"
                        disabled={isSaving}
                        icon="close"
                        onClick={() => onClose({})}
                    />
                </div>
            </div>
            {unresolvableGroup && (
                <Attention
                    alignment="left"
                    className="push--top"
                    type="bad-news"
                    data-testid="unresolvable-group-error"
                >
                    {AMBIGUOUS_PRODUCT_GROUP_ERROR_MESSAGE}
                </Attention>
            )}
            {!!productField?.id && !instancesLoading && !userAccessibleInstances?.length && (
                <Attention alignment="left" className="push-double--top" type="warning">
                    Your organization does not have any Opti ID enabled instances for this product.
                </Attention>
            )}
            {opalAutoProvisioningApplicable && (
                <OpalRoleSuggestionSelection
                    availableGroups={orgOpalProductGroups}
                    opalInstances={opalProduct?.instances || []}
                    className="push--top"
                    currentGroup={additionalOpalGroup.existingGroup}
                    onChange={handleOpalSuggestionChange}
                    skip={excludeOpalRole}
                    value={additionalOpalGroup.selectedGroup}
                    existingAccess={existingOpalAccess}
                />
            )}
        </div>
    );
};

AddProductAccessForm.displayName = "AddProductAccessForm";
